import { Link, useNavigate } from 'react-router-dom';
import { Menu, LogOut } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import { useLogout } from '@/hooks/useAuth';
import { hasRole } from '@/lib/roles';
import { useActiveCompetitionRole } from '@/hooks/useActiveCompetitionRole';
import { Button } from '@/components/ui/Button';

function initialsOf(name: string | undefined, email: string | undefined): string {
  const source = name?.trim() || email || '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function activeRoleLabel(canManage: boolean, isJudge: boolean, isParticipant: boolean): string | null {
  if (canManage) return 'Organizator';
  if (isJudge) return 'Sodnik';
  if (isParticipant) return 'Tekmovalec';
  return null;
}

export function Topbar() {
  const user = useAuthStore((s) => s.user);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const logout = useLogout();
  const navigate = useNavigate();
  const isSuperAdmin = hasRole(user, 'SuperAdmin');
  const { canManage, isJudge, isParticipant } = useActiveCompetitionRole();
  const subtitle = isSuperAdmin ? 'Skrbnik sistema' : activeRoleLabel(canManage, isJudge, isParticipant);

  const handleLogout = async () => {
    await logout.mutateAsync().catch(() => undefined);
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-line bg-surface-1 px-4 md:px-6">
      <button
        type="button"
        className="inline-flex h-10 w-10 items-center justify-center rounded-control text-fg-secondary transition-colors duration-fast hover:bg-surface-2 hover:text-fg md:hidden"
        onClick={toggleSidebar}
        aria-label="Preklopi navigacijo"
      >
        <Menu size={20} />
      </button>
      <div className="hidden md:block" />
      <div className="flex items-center gap-3">
        <Link
          to="/profile"
          title="Moj račun"
          className="flex items-center gap-3 rounded-control p-1 transition-colors duration-fast hover:bg-surface-2"
        >
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-3 text-xs font-semibold text-fg"
            aria-hidden="true"
          >
            {initialsOf(user?.displayName, user?.email)}
          </div>
          <div className="hidden text-right leading-tight sm:block">
            <p className="text-sm font-medium text-fg">{user?.displayName || user?.email}</p>
            {subtitle && <p className="font-mono text-[10px] uppercase tracking-[0.04em] text-fg-muted">{subtitle}</p>}
          </div>
          <span className="sr-only">Moj račun</span>
        </Link>
        <Button variant="secondary" size="sm" onClick={handleLogout} isLoading={logout.isPending}>
          <LogOut size={16} aria-hidden="true" />
          <span className="hidden sm:inline">Odjava</span>
        </Button>
      </div>
    </header>
  );
}
